import { useState, useEffect, useCallback } from 'react'
import { flaskApi } from '@/lib/flaskApi'
import { Button } from '@/components/ui/button'
import { useNotification } from '@refinedev/core'
import { Search, ChevronDown, X, Check, Lock } from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@/components/ui/tooltip'

interface Role {
  id: number; roleName: string; roleType: string; isProtected: boolean
  prohibitedRoleTypes: number[]; permissions: Record<string, string[]>
}
interface Company { id: string; name: string }

const LABEL: Record<string, string> = {
  global_administrator: 'G. Admin', administrator: 'Admin',
  hr_manager: 'HR', recruiter: 'Recr', hiring_manager: 'Hire',
  organization_administrator: 'O. Admin', organization_hr_manager: 'O. HR',
  organization_recruiter: 'O. Recr', organization_hiring_manager: 'O. Hire',
  organization_user: 'O. User', employee: 'Employee',
}
const ORDER = ['global_administrator','administrator','organization_administrator',
  'hr_manager','organization_hr_manager','recruiter','organization_recruiter',
  'hiring_manager','organization_hiring_manager','organization_user','employee']

function hasUserPerms(role: Role): boolean {
  const u = role.permissions?.users
  if (!u || !Array.isArray(u) || u.length === 0) return false
  return u.some((s: any) => typeof s === 'string' && (s.includes(':create') || s.includes(':update')))
}

function effectiveProhibited(role: Role, allRoles: Role[]): Set<number> {
  const raw = role.prohibitedRoleTypes
  if (raw !== null && raw !== undefined) return new Set(Array.isArray(raw) ? raw : [])
  const types = new Set<string>()
  types.add('global_administrator')
  const i = ORDER.indexOf(role.roleType)
  if (i === -1) {
    for (const r of allRoles) {
      if (r.id !== role.id) types.add(r.roleType)
    }
  } else {
    for (let j = 0; j <= i && j < ORDER.length; j++) types.add(ORDER[j])
  }
  const ids = new Set<number>()
  for (const r of allRoles) {
    if (types.has(r.roleType) && r.id !== role.id) ids.add(r.id)
  }
  return ids
}

export function RoleTreePage() {
  const [company, setCompany] = useState<Company | null>(null)
  const [roles, setRoles] = useState<Role[]>([])
  const [searchOpen, setSearchOpen] = useState(false)
  const [q, setQ] = useState('')
  const [co, setCo] = useState<{id:string,name:string}[]>([])
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState<string | null>(null)
  const { open: notify } = useNotification()

  const load = useCallback(async (cid: string) => {
    setLoading(true)
    try {
      const { data } = await flaskApi.get('/role-tree', { params: { company_id: cid } })
      setCompany(data.company)
      setRoles(data.roles || [])
    } catch { notify?.({ type: 'error', message: 'Failed' }) }
    finally { setLoading(false) }
  }, [notify])

  useEffect(() => {
    (async () => {
      try {
        const { data: companies } = await flaskApi.get('/role-tree/companies/search')
        if (companies?.[0]) load(companies[0].id)
      } catch { /* no companies available */ }
    })()
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    const t = setTimeout(async () => {
      try { const { data } = await flaskApi.get('/role-tree/companies/search', { params: { q } }); setCo(data || []) } catch {}
    }, 250)
    return () => clearTimeout(t)
  }, [q])

  const unsaved = roles.filter(r => r.prohibitedRoleTypes === null && !r.isProtected)

  const toggle = async (actorId: number, targetId: number) => {
    const key = `${actorId}-${targetId}`
    setSaving(key)
    const actor = roles.find(r => r.id === actorId)
    if (!actor) return
    const cur = effectiveProhibited(actor, roles)
    const wasBlocked = cur.has(targetId)
    if (wasBlocked) cur.delete(targetId)
    else cur.add(targetId)
    const newList = Array.from(cur)
    const saved = actor.prohibitedRoleTypes?.slice() ?? []
    setRoles(prev => prev.map(r => r.id === actorId ? { ...r, prohibitedRoleTypes: newList } : r))
    try {
      await flaskApi.patch(`/role-tree/${actorId}`, { prohibited_role_types: newList })
    } catch {
      setRoles(prev => prev.map(r => r.id === actorId ? { ...r, prohibitedRoleTypes: saved } : r))
    }
    finally { setSaving(null) }
  }

  const applyDefaults = async () => {
    setSaving('all')
    let ok = 0
    for (const r of unsaved) {
      const def = Array.from(effectiveProhibited(r, roles))
      try {
        await flaskApi.patch(`/role-tree/${r.id}`, { prohibited_role_types: def })
        ok++
        setRoles(prev => prev.map(x => x.id === r.id ? { ...x, prohibitedRoleTypes: def } : x))
      } catch {}
    }
    setSaving(null)
    notify?.({ type: 'success', message: `${ok} roles updated` })
  }

  const sorted = [...roles].sort((a, b) => ORDER.indexOf(a.roleType) - ORDER.indexOf(b.roleType))
  const cols = sorted

  return (
    <div className="space-y-5">
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Role Hierarchy</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            {company ? company.name : 'Select a company to view roles'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {unsaved.length > 0 && (
            <Button variant="outline" size="sm" className="text-xs" onClick={applyDefaults} disabled={saving === 'all'}>
              {saving === 'all' ? 'Saving...' : `Save defaults (${unsaved.length})`}
            </Button>
          )}
          <div className="relative">
            <Button variant="outline" size="sm" className="gap-1.5 text-xs" onClick={() => setSearchOpen(!searchOpen)}>
              <Search size={14} />{company?.name || 'Select Company'}<ChevronDown size={12} />
            </Button>
            {searchOpen && (
              <div className="absolute right-0 top-full mt-1 w-80 bg-card rounded-xl border shadow-xl z-20 p-2">
                <input autoFocus className="w-full px-3 py-2 text-sm rounded-lg border bg-background outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Search companies..." value={q} onChange={e => setQ(e.target.value)} />
                <div className="mt-2 max-h-64 overflow-y-auto">
                  {co.map(c => (
                    <button key={c.id} className="w-full text-left px-3 py-2 text-sm rounded-lg hover:bg-muted"
                      onClick={() => { load(c.id); setCompany({ id: c.id, name: c.name }); setSearchOpen(false); setQ('') }}>
                      {c.name}<span className="ml-2 text-xs text-muted-foreground font-mono">{c.id.slice(0, 12)}...</span>
                    </button>
                  ))}
                  {!co.length && q && <p className="text-xs text-muted-foreground px-3 py-2">No results</p>}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground text-center py-16">Loading...</p>}

      {!loading && !sorted.length && (
        <div className="rounded-xl border border-dashed p-16 text-center text-muted-foreground">
          <p className="text-sm">No roles found for this company</p>
        </div>
      )}

      {!loading && sorted.length > 0 && (
        <div className="rounded-xl border bg-card overflow-hidden">
          <div className="overflow-auto max-h-[calc(100vh-220px)]">
            <table className="w-full border-collapse text-xs">
              <thead>
                <tr className="border-b bg-muted/30">
                  <th className="text-left px-4 py-2.5 font-semibold text-muted-foreground sticky left-0 bg-muted/30 z-10 min-w-[140px]">
                    Role
                  </th>
                  {cols.map(c => (
                    <th key={c.id} className="px-2 py-2.5 text-center font-semibold text-muted-foreground min-w-[52px]">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger>
                            <span className="text-[10px] leading-tight cursor-default">{LABEL[c.roleType] || c.roleType}</span>
                          </TooltipTrigger>
                          <TooltipContent side="top" className="text-xs">
                            {c.roleName}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sorted.map(actor => (
                  <tr key={actor.id} className="border-b last:border-0 hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-2 sticky left-0 bg-card z-10">
                      <div className="font-medium text-foreground text-xs">{actor.roleName}</div>
                      <div className="text-[10px] text-muted-foreground">
                        {LABEL[actor.roleType] || actor.roleType}
                        {actor.isProtected ? ' · protected' : ''}
                        {actor.prohibitedRoleTypes === null && !actor.isProtected ? ' · defaults' : ''}
                      </div>
                    </td>
                    {cols.map(target => {
                      const blocked = effectiveProhibited(actor, roles).has(target.id)
                      const locked = target.roleType === 'global_administrator' && actor.roleType !== 'global_administrator'
                      const canToggle = hasUserPerms(actor) && actor.roleType !== 'global_administrator'
                      const isImmutable = actor.roleType === 'global_administrator'
                      const noPerms = !hasUserPerms(actor) && !isImmutable
                      const cellKey = `${actor.id}-${target.id}`
                      return (
                        <td key={target.id} className="text-center px-1 py-2">
                          {noPerms ? (
                            <span className="w-6 h-6 rounded-md inline-flex items-center justify-center text-muted-foreground/30 text-xs">—</span>
                          ) : (
                          <button
                            disabled={saving === cellKey || !canToggle}
                            onClick={canToggle && !locked ? () => toggle(actor.id, target.id) : undefined}
                            title={locked ? 'Global admin — always blocked' :
                                   isImmutable ? `Global Admin (immutable) can assign ${target.roleName}` :
                                   blocked ? `Click to allow ${actor.roleName} to assign ${target.roleName}` :
                                   `Click to block ${actor.roleName} from assigning ${target.roleName}`}
                            className={cn(
                              'w-6 h-6 rounded-md inline-flex items-center justify-center transition-all',
                              locked
                                ? 'bg-destructive/10 text-destructive/30 cursor-not-allowed'
                                : isImmutable
                                  ? 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600/50 dark:text-emerald-400/50 cursor-not-allowed opacity-60'
                                  : blocked
                                    ? 'bg-destructive/10 text-destructive hover:bg-destructive/20 cursor-pointer'
                                    : 'bg-emerald-50 dark:bg-emerald-950/30 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-100 dark:hover:bg-emerald-950/50 cursor-pointer'
                            )}>
                            {saving === cellKey ? <span className="text-[9px]">...</span> :
                             locked ? <Lock size={10} /> :
                             isImmutable ? <span className="text-sm font-bold leading-none">∞</span> :
                             blocked ? <X size={11} /> : <Check size={11} />}
                          </button>
                          )}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="flex items-center gap-4 text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><Check size={10} className="text-emerald-600" /> can assign (clickable)</span>
        <span className="flex items-center gap-1"><X size={10} className="text-destructive" /> blocked (clickable)</span>
        <span className="flex items-center gap-1"><span className="text-sm font-bold text-emerald-600/50">∞</span> global admin</span>
        <span className="flex items-center gap-1"><Lock size={10} className="text-destructive/30" /> global admin target</span>
        <span className="flex items-center gap-1"><span className="text-xs text-muted-foreground/30">—</span> no user perms</span>
        <span className="ml-auto text-[10px] text-muted-foreground/70">Add users:create or users:update to role permissions to enable toggles</span>
      </div>
    </div>
  )
}
